import React, { useMemo, useCallback } from 'react';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { IssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import RoadmapChangeParentMenu from '@atlassian/jira-software-roadmap-timeline-table-kit/src/ui/change-parent-menu/index.tsx';
import type { Option } from '@atlassian/jira-software-roadmap-timeline-table-kit/src/ui/change-parent-menu/types.tsx';
import type { OnItemDrop } from '@atlassian/timeline-table/common/types/callbacks';
import type { ListItemMenuOptionProps } from '@atlassian/timeline-table/renderers';
import { useIssuesMap } from '../../../../../common/utils/use-issues-map.tsx';
import { useHierarchyStore } from '../../../../../controllers/hierarchy/index.tsx';

type Props = {
	id: IssueId;
	level: number;
	parentId: string;
	setInitialFocusRef?: ListItemMenuOptionProps['setInitialFocusRef'];
	setCloseMenu: ListItemMenuOptionProps['setCloseMenu'];
	updateIssueParent: OnItemDrop;
};

const ReparentMenu = ({
	id,
	parentId,
	level,
	setInitialFocusRef: setFocusRef,
	updateIssueParent,
	setCloseMenu,
}: Props) => {
	const [{ issuesMap }] = useIssuesMap();
	const [{ expandedItems }, { setIssueExpanded }] = useHierarchyStore();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const analyticsEvent = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'option',
	});

	const filteredParentOptions = useMemo(() => {
		const candidateIssues: Option[] = [];
		issuesMap.forEach((value, key) => {
			if (parentId !== key && value.issueType.hierarchyLevel === level + 1) {
				candidateIssues.push({
					id: key,
					iconUrl: value.issueType.iconUrl,
					key: value.key,
					summary: value.summary || '',
					issueType: value.issueType.name,
				});
			}
		});
		return candidateIssues;
	}, [issuesMap, level, parentId]);

	const onOptionSelect = useCallback(
		(event: React.MouseEvent | React.KeyboardEvent, option: Option) => {
			if (!expandedItems[option.id]) {
				setIssueExpanded(option.id, true);
			}
			updateIssueParent(
				id,
				{
					level,
					parentId: option.id,
					isMeatballsAction: true,
				},
				analyticsEvent,
			);

			setCloseMenu(event);
		},
		[analyticsEvent, expandedItems, id, level, setCloseMenu, setIssueExpanded, updateIssueParent],
	);

	return (
		<RoadmapChangeParentMenu
			options={filteredParentOptions}
			onOptionSelect={onOptionSelect}
			inputRef={setFocusRef}
		/>
	);
};

export default ReparentMenu;
